/**
 * Enhanced form input component with validation and error handling for auth forms.
 * Supports both modern and legacy API patterns for backward compatibility.
 * Used across all authentication forms with Supabase integration.
 *
 * IMPORTANT: This component has dual usage patterns:
 * 1. Authentication-specific forms (login, signup, etc.)
 * 2. Email capture forms embedded in content pages (blogs, news articles)
 *
 * The component is used in content pages (news/blog) to capture emails for newsletter
 * subscriptions and content access, allowing users to engage with unrestricted content
 * without requiring a full authentication flow.
 *
 * @module ExtendedAuthInput
 * @see {@link src/components/ui/auth/AuthInput.tsx} - Base input component
 * @see {@link src/components/ui/auth/ErrorText.tsx} - Error display component
 * @see {@link src/components/blog/boostCard/BoostCard.tsx} - Email capture in blog articles
 * @see {@link src/components/blog/topArticles/TopArticles.tsx} - Email capture in article lists
 * @see {@link src/components/news/index.tsx} - Email capture in news detail pages
 * @see {@link src/app/utils/supabase/index.ts} - Supabase client implementation
 * @see {@link src/services/auth.service.ts} - Auth service implementation
 * @see {@link https://supabase.com/docs/guides/auth/auth-helpers/nextjs} - Supabase Auth Helpers for Next.js
 * @see {@link https://react-hook-form.com/docs/usecontroller/controller} - React Hook Form Controller
 * @see {@link https://chakra-ui.com/docs/components/form-control} - Chakra UI Form Control
 */

import React, { useId } from "react";

import {
  Box,
  FormLabel,
  type FormLabelProps,
  type InputProps,
} from "@chakra-ui/react";
import { Input } from "@chakra-ui/react";
import type { Control, FieldErrors, FieldValues, Path } from "react-hook-form";

/**
 * Extended input component for authentication forms with validation
 * @module components/ui/auth/ExtendedAuthInput
 */

interface InputConfig {
  id?: string;
  name?: string;
  type?: string;
  placeholder?: string;
  h?: string | number;
  isDisabled?: boolean;
  // Add any other properties that might be used in the input object
}

interface ExtendedAuthInputProps<T extends FieldValues = FieldValues>
  extends Omit<InputProps, "id"> {
  control: Control<T>;
  errors: FieldErrors<T>;
  label?: string;
  labelBg?: string;
  input?: InputConfig;
  id?: string;
  name?: string;
  type?: string;
  placeholder?: string;
  isDisabled?: boolean;
  styles?: Record<string, any>;
  boxStyles?: Record<string, any>;
  watch?: any;
  error_style?: Record<string, any>;
}

/**
 * Extended input component with form validation
 * @component
 * @template T - Form data type
 */
export default function ExtendedAuthInput<T extends FieldValues = FieldValues>({
  control,
  errors,
  label,
  labelBg,
  input,
  ...props
}: ExtendedAuthInputProps<T>): React.ReactElement {
  // Generate a React unique ID as fallback for missing id
  const fallbackId = useId();

  // Support both usage patterns: props passed via 'input' object OR directly as props
  // This flexible API allows the component to be used in both auth forms and content pages
  const fieldName = input?.name || props.name;
  const inputId = input?.id || props.id || fieldName || fallbackId;
  const inputType = input?.type || props.type || "text";
  const inputPlaceholder = input?.placeholder || props.placeholder;
  const inputDisabled = input?.isDisabled || props.isDisabled || false;

  // Extract remaining props from input object if it exists
  let inputProps = {};
  if (input) {
    // Create a safe copy to avoid TypeScript errors
    inputProps = { ...input };
    // Type assertion to allow property deletion
    const propsToRemove = inputProps as Record<string, any>;
    delete propsToRemove.id;
    delete propsToRemove.name;
    delete propsToRemove.type;
    delete propsToRemove.placeholder;
    delete propsToRemove.isDisabled;
  }

  // Extract custom style props and irrelevant props that shouldn't be passed to the DOM
  const {
    styles,
    boxStyles,
    watch,
    error_style,
    name: propName,
    ...restProps
  } = props;

  // Prepare label props with proper typing
  const labelProps: FormLabelProps = {
    htmlFor: inputId,
    mb: "8px",
  };

  // Only add bg if it's defined
  if (labelBg) {
    labelProps.bg = labelBg;
  }

  // Define the field path to use in the form
  const fieldPath = fieldName || inputId;

  // Prepare input props, ensuring name is properly handled
  const inputFieldProps = {
    id: inputId,
    type: inputType,
    placeholder: inputPlaceholder,
    isDisabled: inputDisabled,
    ...(fieldName ? { name: fieldName } : {}),
  };

  return (
    <Box {...(boxStyles || {})}>
      {label && <FormLabel {...labelProps}>{label}</FormLabel>}
      <Input
        {...inputFieldProps}
        {...(styles ? styles : {})}
        {...inputProps}
        {...restProps}
        backgroundColor="white"
        _hover={{ backgroundColor: "white" }}
        _focus={{ backgroundColor: "white" }}
        {...(fieldPath && control
          ? control.register(fieldPath as Path<T>)
          : {})}
        isInvalid={fieldPath && errors ? !!errors[fieldPath as Path<T>] : false}
      />
    </Box>
  );
}
